import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePOTemplateDnd } from './contextProvider/POTemplateDndContext';
import ImgDropzone from '../../../../Components/ImgDropzone/ImgDropzone';
import noImage from '../../../../assets/images/no-image.png';

export default function POdetails({ isPreviewMode = false }) {
  const { t } = useTranslation('purchase-order');
  const { detailState, setDetailState, companyLogo, clearValidationError } = usePOTemplateDnd();
  const { id } = useParams();
  const [showImageModal, setShowImageModal] = useState(false);
  const imgDropzoneRef = useRef(null);
  const isEditMode = !!id;

  const handleImageUpload = () => {
    setShowImageModal(true);
  };

  const handleImageDelete = () => {
    const isCustomImage = detailState.poImage && detailState.poImage !== companyLogo && !detailState.poImage.includes('base64');

    setDetailState((prevState) => ({
      ...prevState,
      poImage: null,
      originalPOImage: null,
      deleteS3Images: isCustomImage, // Set flag only if deleting a custom S3 image
    }));
  };

  const handleImageModalSubmit = async (action) => {
    if (action === 'close') {
      setShowImageModal(false);
      return;
    }

    try {
      const result = await imgDropzoneRef.current?.showCroppedImg();

      if (result) {
        const { imgRes, orgImage } = result;
        setDetailState((prevState) => ({
          ...prevState,
          poImage: imgRes,
          originalPOImage: orgImage,
          deleteS3Images: false,
        }));
      }

      setShowImageModal(false);
    } catch (error) {
      setShowImageModal(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setDetailState((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    if (name === 'templateName' && detailState.validationErrors?.templateName) {
      clearValidationError('templateName');
    }
  };

  const imgSrc = (img) => {
    if (!img) return companyLogo;
    return img?.includes('https://') || img?.includes('base64')
      ? img
      : `https://${process.env.REACT_APP_BUCKET_NAME}.s3.${process.env.REACT_APP_AWS_REGION}.amazonaws.com/${img}`;
  };

  return (
    <div className="inner-container po-template-name p-24 mb-8">
      <div className="row">
        <div className="col-md-4">
          <h3 className="main-title">{t('PO_TEMPLATE.DETAIL_PAGE.PURCHASE_ORDER')}</h3>
          <div className="form-group">
            <div className="common-input-control">
              <p className="field-name">
                {t('PO_TEMPLATE.DETAIL_PAGE.TEMPLATE_NAME')}
                <span className="asterisk">*</span>
              </p>
              {isPreviewMode ? (
                <div className="form-control-plaintext">{detailState?.templateName || '--'}</div>
              ) : (
                <input
                  type="text"
                  name="templateName"
                  placeholder={t('PO_TEMPLATE.DETAIL_PAGE.TEMPLATE_NAME')}
                  className="form-control"
                  value={detailState?.templateName || ''}
                  onChange={handleInputChange}
                />
              )}
              {detailState.validationErrors?.templateName && <div className="invalid-feedback d-block">{detailState.validationErrors.templateName}</div>}
            </div>
          </div>
          <div className=" flex justify-between items-center">
            <span className="text-sm font-medium text-foreground">{t('PO_TEMPLATE.DETAIL_PAGE.PURCHASE_ORDER_NUMBER')}: </span>
            <span className="text-sm text-muted-foreground">{detailState?.purchaseOrderNumber || ''}</span>
          </div>
          <div className=" flex justify-between items-center">
            <span className="text-sm font-medium text-foreground">{t('PO_TEMPLATE.DETAIL_PAGE.PURCHASE_ORDER_DATE')}: </span>
            <span className="text-sm text-muted-foreground">{detailState?.purchaseOrderDate || ''}</span>
          </div>
        </div>
        <div className="col-md-4  offset-md-4 d-flex align-items-center justify-content-end">
          <div className=" d-flex flex-column mr-8">
            {!isPreviewMode && (
              <>
                <button type="button" aria-label="upload-image" className="btn p-0" onClick={handleImageUpload}>
                  <span className="icon-Upload-1 f-24" />
                </button>
                {detailState?.poImage && (
                  <button type="button" aria-label="delete-image" className="btn p-0" onClick={handleImageDelete}>
                    <span className="icon-Delete text-grey-color f-24" />
                  </button>
                )}
              </>
            )}
          </div>
          <figure className="mb-0 ">
            <img
              src={imgSrc(detailState?.poImage)}
              onError={({ currentTarget }) => {
                currentTarget.onerror = null;
                currentTarget.src = noImage;
              }}
              className="img-fluid po-temp-image"
              alt="Purchase order company logo"
            />
          </figure>
        </div>
      </div>

      {showImageModal && (
        <ImgDropzone
          ref={imgDropzoneRef}
          onSubmit={handleImageModalSubmit}
          fromPOTemplate={showImageModal}
          acceptedFileExtenstion="image/*"
          title="PO Template Image"
          cropSize="FREE"
          image={detailState?.originalPOImage}
          isEditMode={isEditMode}
          replaceItem={{ image: detailState?.originalPOImage }}
        />
      )}
    </div>
  );
}

POdetails.displayName = 'POdetails';

POdetails.propTypes = {
  isPreviewMode: PropTypes.bool,
};

POdetails.defaultProps = {
  isPreviewMode: false,
};
